import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import zhCNDictionary from './locales/zh-CN';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguageCode,
  resolvePreferredLanguage,
  writeLanguageToStorage
} from './utils/languageUtils';

const dictionaryLoaders = Object.freeze({
  'zh-CN': async () => zhCNDictionary,
  'en-US': async () => {
    const module = await import('./locales/en-US');
    return module.default;
  }
});

const supportedLanguages = Object.freeze(Object.keys(dictionaryLoaders));

const dictionaryCache = new Map([
  ['zh-CN', zhCNDictionary]
]);
const dictionaryLoadPromises = new Map();

const getFallbackDictionary = () =>
  dictionaryCache.get(DEFAULT_LANGUAGE) || zhCNDictionary;

const resolveDictionaryLanguage = (language) =>
  normalizeLanguageCode(language, supportedLanguages) || DEFAULT_LANGUAGE;

const isDictionaryLoaded = (language) => {
  const normalizedLanguage = resolveDictionaryLanguage(language);
  return dictionaryCache.has(normalizedLanguage);
};

export const getLoadedDictionary = (language) => {
  const normalizedLanguage = resolveDictionaryLanguage(language);
  return dictionaryCache.get(normalizedLanguage) || getFallbackDictionary();
};

export const loadLanguageDictionary = async (language) => {
  const normalizedLanguage = resolveDictionaryLanguage(language);
  const cachedDictionary = dictionaryCache.get(normalizedLanguage);
  if (cachedDictionary) {
    return cachedDictionary;
  }

  const inFlightPromise = dictionaryLoadPromises.get(normalizedLanguage);
  if (inFlightPromise) {
    return inFlightPromise;
  }

  const loader = dictionaryLoaders[normalizedLanguage];
  if (typeof loader !== 'function') {
    return getFallbackDictionary();
  }

  const loadPromise = Promise.resolve()
    .then(() => loader())
    .then((dictionary) => {
      if (dictionary && typeof dictionary === 'object') {
        dictionaryCache.set(normalizedLanguage, dictionary);
        return dictionary;
      }
      return getFallbackDictionary();
    })
    .catch(() => getFallbackDictionary())
    .finally(() => {
      dictionaryLoadPromises.delete(normalizedLanguage);
    });

  dictionaryLoadPromises.set(normalizedLanguage, loadPromise);
  return loadPromise;
};

const I18nContext = createContext({
  lang: DEFAULT_LANGUAGE,
  setLang: () => {},
  t: (key) => key
});

function interpolate(text, params) {
  if (!params) {
    return text;
  }

  return Object.keys(params).reduce((acc, key) => {
    const escapedKey = String(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const value = params[key] == null ? '' : String(params[key]);
    return acc
      .replace(new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g'), () => value)
      .replace(new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'g'), () => value);
  }, text);
}

const resolveInitialLanguage = (defaultLang) =>
  resolvePreferredLanguage({
    defaultLanguage: defaultLang,
    supportedLanguages
  });

export function I18nProvider({ children, defaultLang }) {
  const [lang, setLangState] = useState(() => resolveInitialLanguage(defaultLang));
  const [dictionaryVersion, setDictionaryVersion] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (isDictionaryLoaded(lang)) {
      return undefined;
    }

    let cancelled = false;

    loadLanguageDictionary(lang).then(() => {
      if (!cancelled && mountedRef.current) {
        setDictionaryVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    writeLanguageToStorage(lang);
  }, [lang]);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.documentElement) {
      return undefined;
    }

    const rootElement = document.documentElement;
    const previousLang = rootElement.lang;
    rootElement.lang = lang;

    return () => {
      if (rootElement.lang === lang) {
        rootElement.lang = previousLang;
      }
    };
  }, [lang]);

  const setLang = useCallback((nextLang) => {
    const normalizedLanguage = normalizeLanguageCode(nextLang, supportedLanguages);
    if (!normalizedLanguage) {
      return;
    }

    setLangState((currentLanguage) => {
      if (currentLanguage === normalizedLanguage) {
        return currentLanguage;
      }

      return normalizedLanguage;
    });
  }, []);

  const t = useCallback((key, params) => {
    const activeDictionary = getLoadedDictionary(lang);
    const fallbackDictionary = getFallbackDictionary();
    const text = activeDictionary[key] || fallbackDictionary[key] || key;
    return interpolate(text, params);
  }, [lang, dictionaryVersion]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export { supportedLanguages };
